import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { formatUsdt } from '@shared/types';

import { Figure } from '@/components/ui/Figure';
import { InlineError } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useTraderBalances } from '@/lib/queries';
import { toMessage } from '@/lib/api';
import { usdt } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * The four figures, and the distinction between them.
 *
 * This panel exists because two misreadings both cost money and support time:
 * a trader who reads «заморожено» as «потеряно» opens a ticket about funds that
 * are doing exactly what they should, and a trader who reads «депозит» as
 * «доступно» tries to withdraw their own collateral and is refused. So each
 * figure carries one sentence, and the sentences do the work — the hero size on
 * «Доступно к выводу» is the second signal, not the first.
 *
 * Only `available` is the trader's to spend. That is stated on the figure
 * itself and repeated on the two that are not.
 */

/** Written once so Обзор and Финансы cannot describe the same figure differently. */
const COPY = {
  available: {
    label: 'Доступно к выводу',
    caption: 'Заработанная комиссия. Это единственные средства, которые можно вывести.',
    short: 'Заработанная комиссия — её можно вывести.',
  },
  deposit: {
    label: 'Депозит',
    caption:
      'Ваш залог. Пока он свободен, платежи направляются вам: под каждый резервируется часть депозита. Это обеспечение, а не заработок.',
    short: 'Залог, под который вам направляют платежи.',
  },
  frozen: {
    label: 'Заморожено',
    caption:
      'Обеспечение по платежам, которые ещё не закрыты. Вернётся в депозит, если платёж отменится или истечёт, и уйдёт мерчанту, когда вы подтвердите получение рублей. Это не списание.',
    short: 'Обеспечение по незакрытым платежам. Это не списание.',
  },
  withdrawn: {
    label: 'Выведено',
    caption: 'За всё время.',
    short: 'За всё время.',
  },
} as const;

export function BalancesPanel({
  variant,
  aside,
  className,
}: {
  variant: 'overview' | 'full';
  /** Overview only: the second cell, owned by the screen (24h earnings). */
  aside?: ReactNode;
  className?: string;
}) {
  const balances = useTraderBalances();

  if (balances.isPending) {
    return (
      <div
        role="status" aria-busy="true"
        className={cn('rounded-md border border-line bg-surface px-5 py-7 sm:px-7', className)}
      >
        <span className="sr-only">Загружаю баланс</span>
        <Skeleton className="h-2.5 w-32" />
        <Skeleton className="mt-4 h-12 w-64" />
        <Skeleton className="mt-3 h-3.5 w-full max-w-prose" />
        {variant === 'overview' ? <FinanceLink className="mt-5" /> : null}
      </div>
    );
  }

  if (balances.isError) {
    return (
      // The link is repeated here on purpose. It is the only route to Финансы,
      // so a failed balance call must not also strand the trader away from the
      // withdrawal form and the statement.
      <div className={className}>
        <InlineError
          message={toMessage(balances.error, 'Баланс недоступен.')}
          onRetry={() => void balances.refetch()}
        />
        {variant === 'overview' ? <FinanceLink className="mt-3" /> : null}
      </div>
    );
  }

  const data = balances.data;
  // A ReactNode can be 0 or the empty string, neither of which `cn` accepts.
  const hasAside = Boolean(aside);

  if (variant === 'full') {
    return (
      <div
        className={cn(
          'grid gap-px overflow-hidden rounded-md border border-line bg-line sm:grid-cols-2',
          className,
        )}
      >
        <div className="bg-surface px-5 py-7 sm:col-span-2 sm:px-7 sm:py-9">
          <Figure
            size="hero"
            label={COPY.available.label}
            value={formatUsdt(data.available)}
            unit="USDT"
            caption={COPY.available.caption}
          />
        </div>

        <div className="bg-surface px-5 py-6 sm:px-7">
          <Figure
            size="md"
            label={COPY.deposit.label}
            value={formatUsdt(data.deposit)}
            unit="USDT"
            caption={COPY.deposit.caption}
          />
        </div>

        <div className="bg-surface px-5 py-6 sm:px-7">
          <Figure
            size="md"
            label={COPY.frozen.label}
            value={formatUsdt(data.frozen)}
            unit="USDT"
            caption={COPY.frozen.caption}
          />
        </div>

        {/* Quiet on purpose: a lifetime total is a fact, not a thing to act on. */}
        <div className="bg-surface px-5 py-5 sm:col-span-2 sm:px-7">
          <Figure
            size="sm"
            label={COPY.withdrawn.label}
            value={usdt(data.withdrawnLifetime)}
            caption={COPY.withdrawn.caption}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid gap-px overflow-hidden rounded-md border border-line bg-line',
        aside ? 'md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]' : '',
        className,
      )}
    >
      <div className="bg-surface px-5 py-7 sm:px-7 sm:py-9">
        <Figure
          size="hero"
          label={COPY.available.label}
          value={formatUsdt(data.available)}
          unit="USDT"
          caption={COPY.available.short}
        />
      </div>

      {aside ? <div className="bg-surface px-5 py-7 sm:px-7 sm:py-9">{aside}</div> : null}

      {/* The two figures that are not the trader's to spend, kept small and
          adjacent so the difference is read as a group rather than hunted for.
          The link is the whole route to Финансы — there is no fourth tab. */}
      <div
        className={cn(
          'flex flex-wrap items-end justify-between gap-x-8 gap-y-4 bg-surface px-5 py-4 sm:px-7',
          hasAside && 'md:col-span-2',
        )}
      >
        <div className="flex min-w-0 flex-wrap gap-x-8 gap-y-4">
          <Figure
            size="sm"
            label={COPY.deposit.label}
            value={usdt(data.deposit)}
            caption={COPY.deposit.short}
          />
          <Figure
            size="sm"
            label={COPY.frozen.label}
            value={usdt(data.frozen)}
            caption={COPY.frozen.short}
          />
        </div>

        <FinanceLink />
      </div>
    </div>
  );
}

/** The only route to Финансы — there is no fourth tab, by design. */
function FinanceLink({ className }: { className?: string }) {
  return (
    <Link
      to="/finance"
      className={cn(
        'focus-ring tap-target inline-flex shrink-0 items-center gap-1.5 rounded-sm',
        'text-sm font-medium text-fg-accent underline underline-offset-2',
        className,
      )}
    >
      Финансы и вывод
      <ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" />
    </Link>
  );
}
